import React, { useState } from 'react';
import { X, ShoppingBag, Trash2, Sparkles, Tag, ArrowRight, ShieldCheck, Plus, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { ADD_ON_ITEMS } from '../data/products';
import { NailArtPreview } from './NailArtPreview';

interface CartDrawerProps {
  onCheckoutClick: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ onCheckoutClick }) => {
  const {
    cartItems,
    selectedAddOns,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    toggleAddOn,
    promoCode,
    applyPromoCode,
    removePromoCode,
    subtotal,
    discountAmount,
    shippingFee,
    taxAmount,
    finalTotal,
    freeShippingProgress,
    freeShippingNeeded,
  } = useCart();

  const [inputCode, setInputCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<{ text: string; isError: boolean } | null>(null);

  if (!isCartOpen) return null;

  const handleApplyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputCode.trim()) return;
    const res = applyPromoCode(inputCode);
    setPromoMessage({ text: res.message, isError: !res.success });
    if (res.success) setInputCode('');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
      <div
        className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between overflow-hidden animate-in slide-in-from-right duration-300 border-l border-[#EADFCF]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-[#F5EBE0] bg-[#FFFDF9] space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-serif text-xl font-bold text-[#292524]">Your Shopping Bag</h2>
            </div>
            <button
              onClick={closeCart}
              aria-label="Close Cart"
              className="p-1.5 rounded-full hover:bg-[#FAF5EE] text-[#78716C] hover:text-[#292524] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Free Shipping Progress Bar */}
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="text-[#854D0E] flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                {freeShippingNeeded > 0
                  ? `Add $${freeShippingNeeded.toFixed(2)} for FREE Shipping`
                  : '🎉 You unlocked FREE US Shipping!'}
              </span>
              <span className="text-[#78716C]">{freeShippingProgress}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-[#FAF5EE] overflow-hidden border border-[#E7DEC8]">
              <div
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#10B981] transition-all duration-500 rounded-full"
                style={{ width: `${freeShippingProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Scrollable Cart Items List */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 divide-y divide-[#F5EBE0]">
          {cartItems.length === 0 ? (
            <div className="text-center py-16 space-y-4">
              <div className="text-4xl text-[#D4AF37]">🛍️</div>
              <h3 className="font-serif text-lg font-bold text-[#292524]">Your bag is empty</h3>
              <p className="text-xs text-[#78716C] max-w-xs mx-auto">
                Explore Kylee’s hand-painted press-on nail designs and find your favorite set!
              </p>
              <button
                onClick={closeCart}
                className="px-6 py-2.5 rounded-xl text-xs font-bold text-white gold-shimmer-btn shadow-md"
              >
                Browse $30 Sets
              </button>
            </div>
          ) : (
            <>
              {/* Product items */}
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.cartId} className="flex gap-3 pt-2">
                    {/* Nail Preview art */}
                    <div className="w-16 h-20 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] flex items-center justify-center shrink-0 overflow-hidden">
                      <NailArtPreview
                        productId={item.product.id}
                        shape={item.shape}
                        className="w-12 h-16 p-1"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="space-y-0.5">
                        <div className="flex items-start justify-between">
                          <h4 className="font-serif text-xs sm:text-sm font-bold text-[#292524] line-clamp-1">
                            {item.product.name}
                          </h4>
                          <button
                            onClick={() => removeFromCart(item.cartId)}
                            className="text-[#A8A29E] hover:text-[#EF4444] transition-colors p-0.5"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-[11px] text-[#78716C]">
                          {item.shape} • {item.length} • Size {item.sizePreset}
                        </p>
                        {item.customMeasurements && (
                          <p className="text-[10px] text-[#854D0E] font-mono">
                            mm: {Object.values(item.customMeasurements).join(', ')}
                          </p>
                        )}
                      </div>

                      {/* Quantity and Price */}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex items-center gap-1.5 bg-[#FAF5EE] p-0.5 rounded-lg border border-[#E7DEC8]">
                          <button
                            onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                            className="w-5 h-5 rounded bg-white text-xs font-bold text-[#292524] flex items-center justify-center hover:bg-[#F3EBE0]"
                          >
                            -
                          </button>
                          <span className="w-5 text-center text-xs font-bold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                            className="w-5 h-5 rounded bg-white text-xs font-bold text-[#292524] flex items-center justify-center hover:bg-[#F3EBE0]"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-serif font-bold text-xs sm:text-sm text-[#B45309]">
                          ${(item.unitPrice * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add-Ons Recommendations */}
              <div className="pt-4 space-y-2.5">
                <span className="text-[11px] font-bold text-[#854D0E] uppercase tracking-wider flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#D4AF37]" />
                  Popular Add-Ons:
                </span>
                
                <div className="space-y-2">
                  {ADD_ON_ITEMS.map((addon) => {
                    const isSelected = selectedAddOns.some((a) => a.id === addon.id);
                    return (
                      <div
                        key={addon.id}
                        onClick={() => toggleAddOn(addon)}
                        className={`p-2.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                          isSelected
                            ? 'bg-[#FEF9C3]/50 border-[#FDE047] shadow-xs'
                            : 'bg-[#FAF5EE]/60 hover:bg-[#FAF5EE] border-[#EADFCF]'
                        }`}
                      >
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-[#292524]">{addon.name}</span>
                            <span className="text-xs font-serif font-bold text-[#B45309]">
                              +${addon.price.toFixed(2)}
                            </span>
                          </div>
                          <p className="text-[10px] text-[#78716C] line-clamp-1">{addon.description}</p>
                        </div>
                        <button
                          type="button"
                          className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs shrink-0 transition-colors ${
                            isSelected
                              ? 'bg-[#10B981] text-white'
                              : 'bg-white border border-[#E7DEC8] text-[#78716C]'
                          }`}
                        >
                          {isSelected ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer & Checkout Area */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t border-[#F5EBE0] bg-[#FFFDF9] space-y-3 shadow-lg">
            
            {/* Promo Code Input */}
            <form onSubmit={handleApplyPromo} className="space-y-1">
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value)}
                  placeholder="Promo Code (Try KYLEE10)"
                  className="flex-1 px-3 py-1.5 rounded-xl bg-white border border-[#E7DEC8] text-xs uppercase text-[#292524] focus:outline-none focus:border-[#D4AF37]"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-xl text-xs font-bold bg-[#FAF5EE] hover:bg-[#F3EBE0] text-[#854D0E] border border-[#E7DEC8]"
                >
                  Apply
                </button>
              </div>
              {promoMessage && (
                <p className={`text-[10px] ${promoMessage.isError ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                  {promoMessage.text}
                </p>
              )}
              {promoCode && (
                <div className="flex items-center justify-between text-xs text-[#10B981] font-semibold bg-[#D1FAE5] px-2 py-1 rounded-lg">
                  <span>Code Applied: {promoCode}</span>
                  <button type="button" onClick={removePromoCode} className="text-[#EF4444] hover:underline text-[10px]">
                    Remove
                  </button>
                </div>
              )}
            </form>

            {/* Calculations Breakdown */}
            <div className="space-y-1.5 text-xs text-[#57534E] pt-1">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-semibold text-[#292524]">${subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-[#10B981]">
                  <span>Discount:</span>
                  <span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Estimated Shipping:</span>
                <span>{shippingFee === 0 ? 'FREE' : `$${shippingFee.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between">
                <span>Estimated Tax (6%):</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base font-serif font-extrabold text-[#292524] pt-1 border-t border-[#F5EBE0]">
                <span>Total:</span>
                <span className="text-[#B45309]">${finalTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout CTA */}
            <button
              onClick={() => {
                closeCart();
                onCheckoutClick();
              }}
              className="w-full py-4 rounded-2xl text-white font-bold text-sm gold-shimmer-btn shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 active:scale-98"
            >
              <span>Proceed to Checkout</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="flex items-center justify-center gap-1.5 text-[10px] text-[#78716C]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
              <span>Stripe Ready • Encrypted & Secure Checkout</span>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};
